import React, { useState, useEffect } from "react";
import {
  Modal,
  Table,
  Card,
  Typography,
  Space,
  Button,
  message,
  Alert,
  Input,
} from "antd";
import { getDirectusClient } from "../../../lib/directus";
import { readItems, updateItem, createItem } from "@directus/sdk";
import moment from "moment";

import { useSelector } from "react-redux";

const { Text, Title } = Typography;

const CajaCloseModal = ({ visible, onClose, cajaId, onCajaClosed }) => {
  const [loading, setLoading] = useState(false);
  const [facturas, setFacturas] = useState({
    cobro: [],
    devoluciones: [],
    credito: [],
    consignaciones: [],
  });
  const [totals, setTotals] = useState({
    totalVentas: 0,
    totalDevoluciones: 0,
    totalNeto: 0,
    totalCredito: 0,
    totalConsignaciones: 0,
  });

  const [novedadModalVisible, setNovedadModalVisible] = useState(false);
  const [novedad, setNovedad] = useState("");
  const [diferencia, setDiferencia] = useState(0);
  const user = useSelector((state) => state.userdata.id);
  const fetchFacturas = async () => {
    if (!cajaId) return;

    setLoading(true);
    try {
      const directus = await getDirectusClient();
      const today = moment().startOf("day");
      const tomorrow = moment().endOf("day");

      const response = await directus.request(
        readItems("facturas", {
          fields: [
            "id",
            "consecutivo",
            "estado",
            "tipo",
            "productos.cantidad",
            "productos.precio_unitario",
            "productos.producto.grupo.impuesto",
            "cliente.first_name",
            "cliente.last_name",
            "vendedor_factura.first_name",
            "vendedor_factura.last_name",
            "date_created",
          ],
          filter: {
            _and: [
              { caja: { _eq: cajaId } },
              {
                date_created: {
                  _between: [today.toISOString(), tomorrow.toISOString()],
                },
              },
            ],
          },
        })
      );

      const consignaciones = await directus.request(
        readItems("consignaciones", {
          fields: ["id", "nombre", "monto", "referencia", "banco.nombre"],
          filter: {
            _and: [
              { caja: { _eq: cajaId } },
              {
                date_created: {
                  _between: [today.toISOString(), tomorrow.toISOString()],
                },
              },
            ],
          },
        })
      );

      const separatedFacturas = {
        cobro: response.filter((f) => f.tipo === 1 && f.estado === "creada"),
        devoluciones: response.filter(
          (f) => f.tipo === 1 && f.estado === "devolucion"
        ),
        credito: response.filter((f) => f.tipo === 2),
      };

      setFacturas({
        ...separatedFacturas,
        consignaciones,
      });

      const calculatedTotals = {
        totalVentas: 0,
        totalDevoluciones: 0,
        totalCredito: 0,
      };

      const calculateInvoiceTotal = (factura) => {
        return factura.productos.reduce((total, producto) => {
          const subtotal = producto.cantidad * producto.precio_unitario;
          const impuesto = producto.producto?.grupo?.impuesto || 0;
          const impuestoValue = subtotal * (impuesto / 100);
          return total + (subtotal + impuestoValue);
        }, 0);
      };

      separatedFacturas.cobro.forEach((factura) => {
        calculatedTotals.totalVentas += calculateInvoiceTotal(factura);
      });

      separatedFacturas.devoluciones.forEach((factura) => {
        calculatedTotals.totalDevoluciones += calculateInvoiceTotal(factura);
      });

      separatedFacturas.credito.forEach((factura) => {
        calculatedTotals.totalCredito += calculateInvoiceTotal(factura);
      });

      const totalConsignaciones = consignaciones.reduce(
        (sum, c) => sum + parseFloat(c.monto),
        0
      );

      setTotals({
        ...calculatedTotals,
        totalNeto:
          calculatedTotals.totalVentas - calculatedTotals.totalDevoluciones,
        totalConsignaciones,
        diferencia: totalConsignaciones - calculatedTotals.totalNeto,
      });
    } catch (error) {
      console.error("Error fetching data:", error);
      message.error("Error al cargar los datos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (visible && cajaId) {
      fetchFacturas();
    }
  }, [visible, cajaId]);

  const handleCloseCaja = async () => {
    try {
      const dif = totals.totalConsignaciones - totals.totalNeto;
      setDiferencia(dif); // Set the diferencia first

      if (dif !== 0 && !novedadModalVisible) {
        setNovedadModalVisible(true);
        return;
      }

      await createLiquidacion();
    } catch (error) {
      console.error("Error:", error);
      message.error("Error al cerrar la caja");
    }
  };

  const createLiquidacion = async () => {
    try {
      const directus = await getDirectusClient();

      const liquidacion = await directus.request(
        createItem("liquidacion", {
          total_facturas: totals.totalVentas,
          total_credito: totals.totalCredito,
          total_devoluciones: totals.totalDevoluciones,
          cajas: {
            create: [{ liquidacion: "+", cajas_id: { id: cajaId } }],
          },
          estado: "creada",
          responsable: user,
          tipo: "local",
          diferencia: diferencia,
          novedades: novedad || null, // Add null check here
        })
      );

      if (facturas.consignaciones.length > 0) {
        const updatePromises = facturas.consignaciones.map((consignacion) =>
          directus.request(
            updateItem("consignaciones", consignacion.id, {
              liquidacion: liquidacion.id,
            })
          )
        );

        await Promise.all(updatePromises);
      }

      await directus.request(
        updateItem("cajas", cajaId, {
          estado: 2,
          fecha_cierre: moment().toISOString(),
          total_ventas: totals.totalNeto,
          tipo: "local",
          liquidada: true,
          cierre_aceptado_por: user,
        })
      );

      message.success("Caja cerrada exitosamente");
      onCajaClosed();
      onClose();
    } catch (error) {
      console.error("Error creating liquidacion:", error);
      message.error("Error al crear la liquidación");
    }
  };

  // First, create a reusable table component with a title
  const InvoiceTable = ({ data, title, loading }) => (
    <Card title={title} style={{ marginBottom: 16 }}>
      <Table
        columns={[
          {
            title: "Consecutivo",
            dataIndex: "consecutivo",
            key: "consecutivo",
          },
          {
            title: "Cliente",
            key: "cliente",
            render: (_, record) =>
              `${record.cliente?.first_name || ""} ${
                record.cliente?.last_name || ""
              }`,
          },
          {
            title: "Vendedor",
            key: "vendedor",
            render: (_, record) =>
              `${record.vendedor_factura?.first_name || ""} ${
                record.vendedor_factura?.last_name || ""
              }`,
          },
          {
            title: "Total",
            key: "total",
            render: (_, record) => {
              const total = record.productos.reduce((acc, producto) => {
                const subtotal = producto.cantidad * producto.precio_unitario;
                const impuesto = producto.producto?.grupo?.impuesto || 0;
                const impuestoValue = subtotal * (impuesto / 100);
                return acc + (subtotal + impuestoValue);
              }, 0);
              return `$${total.toLocaleString()}`;
            },
          },
        ]}
        dataSource={data}
        loading={loading}
        pagination={false}
        scroll={{ y: 200 }}
        summary={(pageData) => {
          const total = pageData.reduce((acc, record) => {
            return (
              acc +
              record.productos.reduce((total, producto) => {
                const subtotal = producto.cantidad * producto.precio_unitario;
                const impuesto = producto.producto?.grupo?.impuesto || 0;
                const impuestoValue = subtotal * (impuesto / 100);
                return total + (subtotal + impuestoValue);
              }, 0)
            );
          }, 0);

          return (
            <Table.Summary.Row>
              <Table.Summary.Cell colSpan={3}>
                <Text strong>Total:</Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell>
                <Text strong>${total.toLocaleString()}</Text>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          );
        }}
      />
    </Card>
  );

  // Add ConsignacionesTable component
  const ConsignacionesTable = ({ data, loading }) => (
    <Card title="Consignaciones" style={{ marginBottom: 16 }}>
      <Table
        columns={[
          {
            title: "Nombre",
            dataIndex: "nombre",
            key: "nombre",
          },
          {
            title: "Monto",
            dataIndex: "monto",
            key: "monto",
            render: (value) => `$${parseFloat(value).toLocaleString()}`,
          },
          {
            title: "Referencia",
            dataIndex: "referencia",
            key: "referencia",
          },
          {
            title: "Banco",
            key: "banco",
            render: (_, record) => record.banco?.nombre,
          },
        ]}
        dataSource={data}
        loading={loading}
        pagination={false}
        scroll={{ y: 200 }}
        summary={(pageData) => {
          const total = pageData.reduce(
            (sum, record) => sum + parseFloat(record.monto),
            0
          );
          return (
            <Table.Summary.Row>
              <Table.Summary.Cell colSpan={1}>
                <Text strong>Total:</Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell>
                <Text strong>${total.toLocaleString()}</Text>
              </Table.Summary.Cell>
              <Table.Summary.Cell colSpan={2} />
            </Table.Summary.Row>
          );
        }}
      />
    </Card>
  );

  // Create a separate NovedadModal component with its own state
  const NovedadModal = ({
    visible,
    onCancel,
    onConfirm,
    diferencia,
    initialNovedad,
  }) => {
    const [localNovedad, setLocalNovedad] = useState(initialNovedad || "");

    useEffect(() => {
      if (visible) {
        setLocalNovedad(initialNovedad || "");
      }
    }, [visible, initialNovedad]);

    const handleConfirm = () => {
      if (localNovedad.trim()) {
        onConfirm(localNovedad.trim());
      }
    };

    return (
      <Modal
        title="Registrar Novedad"
        open={visible}
        onCancel={onCancel}
        onOk={handleConfirm}
        okButtonProps={{ disabled: !localNovedad.trim() }}
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          <Alert
            message={`Diferencia detectada: $${Math.abs(
              diferencia
            ).toLocaleString()}`}
            type="warning"
            showIcon
          />
          <Text>Por favor, explique la razón de la diferencia:</Text>
          <Input.TextArea
            rows={4}
            value={localNovedad}
            onChange={(e) => setLocalNovedad(e.target.value)}
            placeholder="Escriba la novedad aquí..."
            required
          />
        </Space>
      </Modal>
    );
  };

  // Update the parent component's handling
  const handleNovedadConfirm = (novedadText) => {
    setNovedad(novedadText);
    setNovedadModalVisible(false);
    createLiquidacion();
  };

  return (
    <>
      <Modal
        title="Cierre de Caja"
        open={visible}
        onCancel={onClose}
        width={1200}
        footer={[
          <Button key="cancel" onClick={onClose}>
            Cancelar
          </Button>,
          <Button
            key="close"
            type="primary"
            danger
            onClick={handleCloseCaja}
            loading={loading}
          >
            Cerrar Caja
          </Button>,
        ]}
      >
        <Space direction="vertical" style={{ width: "100%" }}>
          <Card>
            <Space direction="vertical">
              <Title level={5}>Resumen del Día</Title>
              <Text>
                Total Ventas Contado:{" "}
                <Text strong>${totals.totalVentas.toLocaleString()}</Text>
              </Text>
              <Text>
                Total Devoluciones:{" "}
                <Text type="danger" strong>
                  ${totals.totalDevoluciones.toLocaleString()}
                </Text>
              </Text>
              <Text>
                Total Neto en Caja:{" "}
                <Text type="success" strong>
                  ${totals.totalNeto.toLocaleString()}
                </Text>
              </Text>
              <Text>
                Total Consignaciones:{" "}
                <Text type="success" strong>
                  ${totals.totalConsignaciones.toLocaleString()}
                </Text>
              </Text>
              <Text type="secondary">
                Total Ventas Crédito:{" "}
                <Text strong>${totals.totalCredito.toLocaleString()}</Text>
              </Text>
            </Space>
          </Card>

          <InvoiceTable
            title="Facturas de Contado"
            data={facturas.cobro.map((f) => ({ ...f, key: `cobro-${f.id}` }))}
            loading={loading}
          />

          <InvoiceTable
            title="Devoluciones"
            data={facturas.devoluciones.map((f) => ({
              ...f,
              key: `dev-${f.id}`,
            }))}
            loading={loading}
          />

          <InvoiceTable
            title="Facturas a Crédito"
            data={facturas.credito.map((f) => ({ ...f, key: `cred-${f.id}` }))}
            loading={loading}
          />

          <ConsignacionesTable
            data={facturas.consignaciones.map((c) => ({
              ...c,
              key: `cons-${c.id}`,
            }))}
            loading={loading}
          />
        </Space>
      </Modal>

      <NovedadModal
        visible={novedadModalVisible}
        onCancel={() => {
          setNovedadModalVisible(false);
          setNovedad("");
        }}
        onConfirm={handleNovedadConfirm}
        diferencia={diferencia}
        initialNovedad={novedad}
      />
    </>
  );
};

export default CajaCloseModal;
